import { useCallback } from 'react';
import { useAuthStore } from '../../../../contexts/auth/useAuthStore';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';
import { useUser } from '../../../../hooks/useUser';

// Webhook URLs for different stages
const WEBHOOK_URLS: Record<string, string> = {
  // Stage 2 - Website Branding
  'Stage 2 - Website Branding Complete': 'https://hooks.zapier.com/hooks/catch/12395623/uz1cw0m/',
  'Stage 2 - GoDaddy Access Shared': 'https://hooks.zapier.com/hooks/catch/12395623/ufm5xan/',

  // Stage 3 - AI Assistant Setup (Twilio)
  'Stage 3 - Twilio Setup Complete': 'https://hooks.zapier.com/hooks/catch/12395623/uzdthcv/',

  // Stage 4 - MLS Licensing Agreement
  'Stage 4 - Step 1: MLS Licensing Agreement':
    'https://hooks.zapier.com/hooks/catch/12395623/uzkk41d/',

  // Stage 4 - IDX Domain Access Shared (same as Stage 2 GoDaddy access)
  'Stage 4 - IDX Domain Access Shared': 'https://hooks.zapier.com/hooks/catch/12395623/ufm5xan/',

  // Stage 4 - DNS Records Added Confirmation (user confirms they added DNS records before launch)
  'Stage 4 - DNS Records Added Confirmation':
    'https://hooks.zapier.com/hooks/catch/12395623/uatne2w/',

  // Stage 4.2.1 - IDX Site Setup Complete (launched)
  'Stage 4 - Step 2: IDX Site Setup Complete': 'https://hooks.zapier.com/hooks/catch/12395623/uzj7i7n/',

  // Stage 4.2.2 - IDX Site Setup Skipped
  'Stage 4 - Step 2: IDX Site Setup (Skipped)':
    'https://hooks.zapier.com/hooks/catch/12395623/uzj7aa2/',

  // Stage 5 - CRM Integrations
  'Stage 5 - Integrations (CRM Setup)': 'https://hooks.zapier.com/hooks/catch/12395623/uf199zu/',

  // Revive Flow Tour - Completed or Skipped
  'Revive Tour - Completed': 'https://hooks.zapier.com/hooks/catch/12395623/uf199zu/',
  'Revive Tour - Skipped': 'https://hooks.zapier.com/hooks/catch/12395623/uf199zu/',

  // Onboarding Complete - Final step when user clicks "Create Your First Flow"
  'Onboarding Complete': 'https://hooks.zapier.com/hooks/catch/12395623/ufm843b/',
};

interface HubspotData {
  contact_id?: string;
  contact_url?: string;
  portal_id?: string;
}

interface WebhookPayload {
  stage: string;
  organizationName: string;
  organizationId: string;
  websiteUrl: string;
  hubspotContactUrl: string;
  dateTime: string;
  // User info
  userEmail: string;
  userFullName: string;
  userPhone: string;
  // Legacy field for backwards compatibility
  user: string;
  data: any;
}

export const useOnboardingWebhook = () => {
  const { user: authUser } = useAuthStore();
  const { user: firestoreUser } = useUser();
  const { organization } = useOrganizationStore();

  const sendWebhook = useCallback(
    async (stageName: string, data: any) => {
      try {
        // Get the appropriate webhook URL for this stage
        const webhookUrl = WEBHOOK_URLS[stageName];

        // Skip if no webhook URL configured for this stage
        if (!webhookUrl) {
          console.log('No webhook configured for stage:', stageName);
          return;
        }

        // Get HubSpot contact URL from organization data
        const hubspotData = organization?.hubspot as HubspotData | undefined;

        // Get website URL from organization - check multiple possible locations
        const onboardingData = (organization as any)?.onboardingData;
        const domainName = onboardingData?.website?.domain_name; // Domain from branding step (bare domain)
        const idxNewDomain = onboardingData?.idx?.site_data?.new_domain; // Domain from IDX step (when Stage 2 skipped)
        const remiWebsiteUrl = onboardingData?.remi?.website_url; // Website from Remi setup
        const rootWebsite = organization?.website; // Root level website

        // Format domain_name with https:// if it's a bare domain
        const formatUrl = (url: string | undefined) => {
          if (!url) return '';
          if (url.startsWith('http://') || url.startsWith('https://')) return url;
          return `https://${url}`;
        };

        const websiteUrl =
          (domainName ? formatUrl(domainName) : '') ||
          (idxNewDomain ? formatUrl(idxNewDomain) : '') ||
          remiWebsiteUrl ||
          rootWebsite ||
          '';

        // Get user info - prefer Firestore user data (has phoneNumber), fallback to auth user
        const userEmail = firestoreUser?.email || authUser?.email || '';
        const userFullName =
          firestoreUser?.displayName ||
          (firestoreUser?.firstName && firestoreUser?.lastName
            ? `${firestoreUser.firstName} ${firestoreUser.lastName}`
            : firestoreUser?.firstName) ||
          (authUser as any)?.displayName ||
          '';
        const userPhone = firestoreUser?.phoneNumber || '';

        const webhookData: WebhookPayload = {
          stage: stageName,
          organizationName: data.org_name || organization?.org_name || '',
          organizationId: organization?.id || '',
          websiteUrl: websiteUrl,
          hubspotContactUrl: hubspotData?.contact_url || '',
          dateTime: new Date().toISOString(),
          // User info from Firestore user
          userEmail,
          userFullName,
          userPhone,
          // Legacy field for backwards compatibility
          user: userEmail,
          data: data,
        };

        await fetch(webhookUrl, {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(webhookData),
        });

        // Also log to console for debugging
        console.log('Webhook sent to:', webhookUrl, webhookData);
      } catch (error) {
        console.error('Webhook error:', error);
        // Don't block submission on webhook failure
      }
    },
    [authUser, firestoreUser, organization]
  );

  return { sendWebhook };
};
